<template>
  <div
    class="insights-performance-highlights-widget"
    :class="{ 'no-data': showPlaceholder || !hasHighlights }"
  >
    <InsightsNoData
      v-if="showPlaceholder"
      :type="emptyReason"
    >
      <template #title> Discover current highlights </template>
      <template #description> There's no past data available yet. We're working on making it available soon!</template>
      <template #button>
        <AppButton
          class="mt-20"
          type="primary"
          @click="openDefaultPage"
        >
          Check current highlights
        </AppButton>
      </template>
    </InsightsNoData>
    <template v-else>
      <div class="widget-title mb-20">
        Significant changes in behavior
        <i
          v-if="isReady"
          v-tooltip
          class="hi hi-info-filled ml-5"
          :title="tooltipText"
        />
      </div>
      <AppCarousel
        v-if="!isReady && isFirstLoad"
        key="loading"
      >
        <InsightsPerformanceHighlightsCardLoading
          v-for="i in 3"
          :key="i"
        />
      </AppCarousel>
      <template v-else>
        <AppCarousel
          v-if="hasHighlights"
          key="main"
          @click="onItemClick"
          @slide-click="onSlideClick"
        >
          <InsightsPerformanceHighlightsCard
            v-for="(user, index) in performanceHighlights.data"
            :key="`${user.id}-${index}`"
            :highlights-data="user"
            :time-frame="currentFixedRange?.id || ''"
            @open-dialog="openDialog"
            @redirect-to-team-member="redirectToTeamMember"
          />
        </AppCarousel>
        <InsightsNoData
          v-else
          :type="emptyReason"
        >
          <template #title> No significant changes recorded</template>
          <template #description> There were no significant changes in behavior for the selected time period</template>
        </InsightsNoData>
      </template>
      <InsightsPerformanceHighlightsDialog
        v-if="isDialogOpen"
        :show="isDialogOpen"
        :highlights-data="currentDialogHighlightsData"
        :type="currentDialogHighlightsData.type"
        :current-range="currentFixedRange"
        @close="closeDialog"
      />
    </template>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { get } from 'lodash-es';
import { mapState, mapGetters } from 'vuex';
import AppCarousel from '~/components/AppCarousel.vue';
import InsightsPerformanceHighlightsCard from '~/components/insights/InsightsPerformanceHighlightsCard.vue';
import InsightsPerformanceHighlightsCardLoading from '~/components/insights/InsightsPerformanceHighlightsCardLoading.vue';
import InsightsPerformanceHighlightsDialog from '~/components/insights/InsightsPerformanceHighlightsDialog.vue';
import { INSIGHTS } from '~/app_constants';

import { Insights, InsightsFilters, InsightsTracking, InsightsDateRanges } from '~/mixins';

const PAST_RANGE_IDS = ['yesterday', 'last-week', 'last-month'];

export default {
  name: 'InsightsPerformanceHighlightsWidget',
  components: {
    AppCarousel,
    InsightsPerformanceHighlightsCard,
    InsightsPerformanceHighlightsCardLoading,
    InsightsPerformanceHighlightsDialog,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, InsightsFilters, InsightsTracking, InsightsDateRanges],
  data: () => ({
    widgetKey: INSIGHTS.api.performanceHighlights,
    isDialogOpen: false,
    currentDialogHighlightsData: null,
    highlightsShown: false,
    title: 'Highlights',
  }),
  computed: {
    ...mapState({
      performanceHighlights: (state) => get(state.widgets, INSIGHTS.api.performanceHighlights, null),
      timeFrame: (state) => get(state.filters, 'date', null),
      organizationID: (state) => get(state.organization, 'id', null),
      organizationZone: (state) => get(state.organization, 'time_zone.for', null),
    }),
    ...mapGetters(['selectedFilter', 'startWeekOn', 'activeTimeZone']),
    // The widget's data is anchored to org-calendar weeks/months, but the range may come from the
    // picker in the selected zone or from the default window in the org zone: accept either, and
    // prefer a current range when the two zones are on different calendar days.
    currentFixedRange() {
      const matches = [
        this.matchFixedRange(this.dateRanges),
        this.organizationZone && this.matchFixedRange(this.buildDateRanges(this.now(this.organizationZone))),
      ].filter(Boolean);

      return matches.find(({ id }) => !PAST_RANGE_IDS.includes(id)) || matches[0];
    },
    hasHighlights() {
      return this.performanceHighlights?.data?.length;
    },
    tooltipText() {
      const interval = this.currentFixedRange.label.includes('month') ? 'month' : 'week';

      return `Spot significant changes in behaviors from teams and members. Cards compare last ${interval}'s averages against the average of the previous 3 ${interval}s. Updates every first day of the ${interval}.`;
    },
    showPlaceholder() {
      return !this.currentFixedRange || PAST_RANGE_IDS.includes(this.currentFixedRange.id);
    },
  },

  updated() {
    if (!this.highlightsShown) {
      this.trackAnalytics('Insights highlights shown', { num_highlights: this.performanceHighlights?.data?.length });

      this.highlightsShown = true;
    }
  },
  methods: {
    onItemClick(index) {
      const currentHighlight = this.performanceHighlights?.data[index];

      this.openDialog(currentHighlight);
    },
    openDialog(highlight) {
      this.currentDialogHighlightsData = { ...highlight, timeFrame: this.timeFrame };
      this.isDialogOpen = true;

      const main = highlight.cards.filter((card) => card.significant)[0];

      this.trackAnalytics('Insights highlights details', {
        label: highlight.name,
        metric_delta: main?.change,
        metric_type: main?.metric,
        scope: highlight.type,
      });
    },
    closeDialog(args) {
      this.isDialogOpen = false;
      this.currentDialogHighlightsData = null;

      if (args) {
        const { id, type } = args;
        const member = `${id}_${type}_id`;

        this.setPreviousView({ previousView: this.selectedFilter, currentView: member });
        this.setSelectedFilter(member);
        this.setMembersFilter(member);
      }
    },
    redirectToTeamMember({ id, type }) {
      // Follow the mixin filter format
      const member = `${id}_${type}_id`;

      this.setPreviousView({ previousView: this.selectedFilter, currentView: member });
      this.setSelectedFilter(member);
      this.setMembersFilter(member);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onSlideClick(direction) {
      this.trackAnalytics('Insights highlights arrow', { direction });
    },
    openDefaultPage() {
      window.location.href = `${window.location.origin}${window.location.pathname}`;
    },
  },
};
</script>
